import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import { Eye, EyeOff } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const Register = () => {
  const { register: registerAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [phoneVerified, setPhoneVerified] = React.useState(false);
  const [verificationCode, setVerificationCode] = React.useState('');
  const [isSendingCode, setIsSendingCode] = React.useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = React.useState(false);
  const [codeSent, setCodeSent] = React.useState(false);
  const [resendCooldown, setResendCooldown] = React.useState(0);
  const [emailCheckStatus, setEmailCheckStatus] = React.useState(null);
  const [isCheckingEmail, setIsCheckingEmail] = React.useState(false);

  // Get return URL from location state
  const returnUrl = location.state?.returnUrl || '/dashboard';

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const password = watch('password');
  const phoneValue = watch('phone');
  const emailValue = watch('email');

  React.useEffect(() => {
    if (resendCooldown <= 0) return undefined;

    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  React.useEffect(() => {
    setPhoneVerified(false);
    setCodeSent(false);
    setVerificationCode('');
  }, [phoneValue]);

  React.useEffect(() => {
    setEmailCheckStatus(null);
  }, [emailValue]);

  const handleCheckEmail = async () => {
    const email = (emailValue || '').trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
      toast.error('이메일을 입력해주세요');
      return;
    }

    if (!emailRegex.test(email)) {
      setEmailCheckStatus('invalid');
      toast.error('유효한 이메일 형식이 아닙니다');
      return;
    }

    setIsCheckingEmail(true);
    try {
      const response = await api.get(`/api/auth/check-email?email=${encodeURIComponent(email)}`);
      setEmailCheckStatus(response.data.available ? 'available' : 'duplicate');
      if (response.data.available) {
        toast.success('사용 가능한 이메일입니다');
      } else {
        toast.error('이미 사용 중인 이메일입니다');
      }
    } catch (error) {
      if (error.response?.data?.available === false) {
        setEmailCheckStatus('duplicate');
      } else {
        setEmailCheckStatus('invalid');
      }
      toast.error(error.response?.data?.message || '이메일 확인에 실패했습니다');
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const handleSendCode = async () => {
    if (!phoneValue || errors.phone) {
      toast.error('올바른 전화번호를 입력해주세요');
      return;
    }

    setIsSendingCode(true);
    try {
      const res = await api.post('/api/auth/phone/send-code', { phone: phoneValue });
      setCodeSent(true);
      setResendCooldown(60);
      toast.success(res.data.message);
    } catch (error) {
      const retryAfter = error.response?.data?.retryAfter;
      if (retryAfter) {
        setResendCooldown(retryAfter);
      }
      toast.error(error.response?.data?.message || '인증번호 발송에 실패했습니다');
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('인증번호 6자리를 입력해주세요');
      return;
    }

    setIsVerifyingCode(true);
    try {
      await api.post('/api/auth/phone/verify-code', {
        phone: phoneValue,
        code: verificationCode,
      });
      setPhoneVerified(true);
      toast.success('전화번호 인증이 완료되었습니다');
    } catch (error) {
      toast.error(error.response?.data?.message || '인증번호 확인에 실패했습니다');
    } finally {
      setIsVerifyingCode(false);
    }
  };

  const onSubmit = async (data) => {
    if (emailCheckStatus !== 'available') {
      toast.error('이메일 중복 확인을 완료해주세요');
      return;
    }

    if (!phoneVerified) {
      toast.error('전화번호 인증을 완료해주세요');
      return;
    }

    setIsLoading(true);
    try {
      const success = await registerAuth(data);
      if (success) {
        navigate(returnUrl);
      }
    } catch (error) {
      console.error('Registration error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-lg">
        <div className="flex justify-center">
          <div className="w-12 h-12 bg-primary-500 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-xl">E</span>
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          새 계정 만들기
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          또는{' '}
          <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
            기존 계정으로 로그인
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-lg">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                이름
              </label>
              <div className="mt-1">
                <input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  {...register('name', {
                    required: '이름을 입력해주세요',
                    minLength: {
                      value: 2,
                      message: '이름은 최소 2자 이상이어야 합니다',
                    },
                  })}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="이름을 입력하세요"
                />
                {errors.name && (
                  <p className="mt-2 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                이메일 주소
              </label>
              <div className="mt-1 flex gap-2">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  {...register('email', {
                    required: '이메일을 입력해주세요',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: '유효한 이메일 주소를 입력해주세요',
                    },
                  })}
                  className={`appearance-none block flex-1 px-3 py-2 border rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                    emailCheckStatus === 'available'
                      ? 'border-green-400'
                      : emailCheckStatus === 'duplicate' || emailCheckStatus === 'invalid'
                        ? 'border-red-400'
                        : 'border-gray-300'
                  }`}
                  placeholder="이메일을 입력하세요"
                />
                <button
                  type="button"
                  onClick={handleCheckEmail}
                  disabled={isCheckingEmail || !emailValue || errors.email}
                  className="whitespace-nowrap px-4 py-2 border border-primary-600 text-sm font-medium rounded-md text-primary-600 hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCheckingEmail ? '확인 중...' : '중복 확인'}
                </button>
              </div>
              {errors.email && (
                <p className="mt-2 text-sm text-red-600">{errors.email.message}</p>
              )}
              {emailCheckStatus === 'available' && (
                <p className="mt-2 text-sm text-green-600">사용 가능한 이메일입니다</p>
              )}
              {emailCheckStatus === 'duplicate' && (
                <p className="mt-2 text-sm text-red-600">이미 사용 중인 이메일입니다</p>
              )}
              {emailCheckStatus === 'invalid' && (
                <p className="mt-2 text-sm text-red-600">유효한 이메일 형식이 아닙니다</p>
              )}
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                전화번호
              </label>
              <div className="mt-1 flex gap-2">
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  required
                  disabled={phoneVerified}
                  {...register('phone', {
                    required: '전화번호를 입력해주세요',
                    pattern: {
                      value: /^01[0-9]{1}-?[0-9]{4}-?[0-9]{4}$/,
                      message: '올바른 휴대폰 번호를 입력해주세요 (예: 010-1234-5678)',
                    },
                  })}
                  className="appearance-none block flex-1 px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:bg-gray-100"
                  placeholder="010-1234-5678"
                />
                <button
                  type="button"
                  onClick={handleSendCode}
                  disabled={phoneVerified || isSendingCode || resendCooldown > 0 || !phoneValue || errors.phone}
                  className="whitespace-nowrap px-4 py-2 border border-primary-600 text-sm font-medium rounded-md text-primary-600 hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSendingCode
                    ? '발송 중...'
                    : resendCooldown > 0
                      ? `${resendCooldown}초`
                      : codeSent
                        ? '재발송'
                        : '인증번호 발송'}
                </button>
              </div>
              {errors.phone && (
                <p className="mt-2 text-sm text-red-600">{errors.phone.message}</p>
              )}

              {codeSent && !phoneVerified && (
                <div className="mt-3 flex gap-2">
                  <input
                    id="verificationCode"
                    name="verificationCode"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                    className="appearance-none block flex-1 px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="인증번호 6자리"
                  />
                  <button
                    type="button"
                    onClick={handleVerifyCode}
                    disabled={isVerifyingCode || verificationCode.length !== 6}
                    className="whitespace-nowrap px-4 py-2 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isVerifyingCode ? '확인 중...' : '확인'}
                  </button>
                </div>
              )}

              {phoneVerified && (
                <p className="mt-2 text-sm text-green-600">전화번호 인증이 완료되었습니다.</p>
              )}
            </div>

            <div>
              <label htmlFor="gender" className="block text-sm font-medium text-gray-700">
                성별
              </label>
              <div className="mt-1">
                <select
                  id="gender"
                  name="gender"
                  required
                  {...register('gender', {
                    required: '성별을 선택해주세요',
                  })}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                >
                  <option value="">성별을 선택하세요</option>
                  <option value="male">남성</option>
                  <option value="female">여성</option>
                  <option value="other">기타</option>
                </select>
                {errors.gender && (
                  <p className="mt-2 text-sm text-red-600">{errors.gender.message}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                주소
              </label>
              <div className="mt-1">
                <textarea
                  id="address"
                  name="address"
                  rows="3"
                  required
                  {...register('address', {
                    required: '주소를 입력해주세요',
                    minLength: {
                      value: 5,
                      message: '주소는 최소 5자 이상이어야 합니다',
                    },
                  })}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm resize-none"
                  placeholder="전체 주소를 입력하세요"
                />
                {errors.address && (
                  <p className="mt-2 text-sm text-red-600">{errors.address.message}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700">
                생년월일
              </label>
              <div className="mt-1">
                <input
                  id="dateOfBirth"
                  name="dateOfBirth"
                  type="date"
                  required
                  {...register('dateOfBirth', {
                    required: '생년월일을 입력해주세요',
                    validate: (value) => {
                      const selectedDate = new Date(value);
                      const today = new Date();
                      const age = today.getFullYear() - selectedDate.getFullYear();
                      if (age < 14) {
                        return '만 14세 이상만 가입 가능합니다';
                      }
                      if (selectedDate > today) {
                        return '미래 날짜는 선택할 수 없습니다';
                      }
                      return true;
                    },
                  })}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
                {errors.dateOfBirth && (
                  <p className="mt-2 text-sm text-red-600">{errors.dateOfBirth.message}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                비밀번호
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  {...register('password', {
                    required: '비밀번호를 입력해주세요',
                    minLength: {
                      value: 6,
                      message: '비밀번호는 최소 6자 이상이어야 합니다',
                    },
                  })}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm pr-10"
                  placeholder="비밀번호를 입력하세요"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                {errors.password && (
                  <p className="mt-2 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                비밀번호 확인
              </label>
              <div className="mt-1 relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  {...register('confirmPassword', {
                    required: '비밀번호를 다시 입력해주세요',
                    validate: (value) => value === password || '비밀번호가 일치하지 않습니다',
                  })}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm pr-10"
                  placeholder="비밀번호를 다시 입력하세요"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                {errors.confirmPassword && (
                  <p className="mt-2 text-sm text-red-600">{errors.confirmPassword.message}</p>
                )}
              </div>
            </div>



            <div className="flex items-center">
              <input
                id="agree"
                name="agree"
                type="checkbox"
                required
                {...register('agree', {
                  required: '이용약관에 동의해주세요',
                })}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="agree" className="ml-2 block text-sm text-gray-900">
                <span className="text-gray-600">
                  <Link to="/terms" className="text-primary-600 hover:text-primary-500">
                    이용약관
                  </Link>
                  과{' '}
                  <Link to="/privacy" className="text-primary-600 hover:text-primary-500">
                    개인정보처리방침
                  </Link>
                  에 동의합니다
                </span>
              </label>
            </div>
            {errors.agree && (
              <p className="mt-2 text-sm text-red-600">{errors.agree.message}</p>
            )}

            <div>
              <button
                type="submit"
                disabled={isLoading || !phoneVerified || emailCheckStatus !== 'available'}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? '계정 생성 중...' : '계정 만들기'}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">또는</span>
              </div>
            </div>

            <div className="mt-6">
              <div>
                <button
                  type="button"
                  className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  <span className="ml-2">Google로 가입</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register; 